import * as React from "react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

type CountryCode = "RU" | "CN";

interface CountryConfig {
  code: CountryCode;
  dialCode: string;
  flag: string;
  mask: string;
  maxDigits: number;
}

const COUNTRIES: Record<CountryCode, CountryConfig> = {
  RU: {
    code: "RU",
    dialCode: "+7",
    flag: "🇷🇺",
    mask: "(___) ___-__-__",
    maxDigits: 10,
  },
  CN: {
    code: "CN",
    dialCode: "+86",
    flag: "🇨🇳",
    mask: "___ ____ ____",
    maxDigits: 11,
  },
};

function applyMask(digits: string, mask: string): string {
  let result = "";
  let digitIndex = 0;
  for (const ch of mask) {
    if (digitIndex >= digits.length) break;
    if (ch === "_") {
      result += digits[digitIndex++];
    } else {
      result += ch;
    }
  }
  return result;
}

function extractDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function getCountryFromValue(value: string): CountryCode | null {
  if (value.startsWith("+86")) return "CN";
  if (value.startsWith("+7")) return "RU";

  const digits = value.replace(/\D/g, "");

  if (digits.startsWith("8") && digits.length === 11) return "RU";

  return null;
}

interface PhoneInputProps {
  value: string;
  onChange: (fullPhone: string) => void;
  onBlur?: () => void;
  className?: string;
  defaultCountry?: CountryCode;
  id?: string;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
}

export function PhoneInput({
  value,
  onChange,
  onBlur,
  className,
  defaultCountry,
  id,
  disabled,
  error,
  required,
}: PhoneInputProps) {
  const [country, setCountry] = React.useState<CountryCode>(() => {
    return getCountryFromValue(value) ?? defaultCountry ?? "RU";
  });
  const [prevValue, setPrevValue] = React.useState(value);

  if (value !== prevValue) {
    setPrevValue(value);
    const detectedCountry = getCountryFromValue(value);
    if (detectedCountry && detectedCountry !== country) {
      setCountry(detectedCountry);
    }
  }

  const config = COUNTRIES[country];

  const rawDigits = React.useMemo(() => {
    const stripped = value.replace(/\D/g, "");
    if (value.startsWith("+86")) return stripped.slice(2);
    if (value.startsWith("+7")) return stripped.slice(1);
    if (stripped.startsWith("8") && stripped.length === 11) return stripped.slice(1);
    return stripped;
  }, [value]);

  const displayValue = applyMask(rawDigits, config.mask);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = extractDigits(e.target.value).slice(0, config.maxDigits);
    onChange(`${config.dialCode}${digits}`);
  };

  const handleCountryChange = (newCountry: string) => {
    const cc = newCountry as CountryCode;
    setCountry(cc);
    const newConfig = COUNTRIES[cc];
    const digits = rawDigits.slice(0, newConfig.maxDigits);
    onChange(`${newConfig.dialCode}${digits}`);
  };

  return (
    <div
      data-error={error || undefined}
      data-disabled={disabled || undefined}
      className={cn(
        "flex h-12 w-full items-center rounded-2xl border border-border bg-surface font-sans",
        "transition-[border-color,box-shadow,background-color]",
        "focus-within:border-[hsl(var(--border-focus))] focus-within:ring-1 focus-within:ring-[hsl(var(--border-focus))]",
        "data-[error]:border-2 data-[error]:border-[hsl(var(--destructive))] data-[error]:ring-0",
        "data-[disabled]:cursor-not-allowed data-[disabled]:bg-state-disabled-bg/[0.05]",
        className,
      )}
    >
      <Select value={country} onValueChange={handleCountryChange} disabled={disabled}>
        <SelectTrigger
          aria-label="Код страны"
          className={cn(
            "h-full w-auto shrink-0 gap-1.5 rounded-none rounded-l-2xl border-0 bg-transparent py-0 pl-4 pr-2.5 text-base leading-none text-foreground",
            "ring-offset-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0",
            "[&>span]:line-clamp-none [&>span]:flex [&>span]:h-full [&>span]:translate-y-[-1px] [&>span]:items-center [&>span]:gap-1.5 [&>span]:leading-none",
          )}
        >
          <SelectValue>
            <span className="text-base leading-none">{config.flag}</span>
            <span className="font-normal leading-none tabular-nums">{config.dialCode}</span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="RU">
            <span className="flex items-center gap-1.5">
              <span>🇷🇺</span>
              <span className="tabular-nums">+7</span>
            </span>
          </SelectItem>
          <SelectItem value="CN">
            <span className="flex items-center gap-1.5">
              <span>🇨🇳</span>
              <span className="tabular-nums">+86</span>
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
      <span aria-hidden className="mx-1 h-6 w-px shrink-0 bg-border" />
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        value={displayValue}
        onChange={handleInputChange}
        onBlur={onBlur}
        placeholder={config.mask}
        disabled={disabled}
        required={required}
        className={cn(
          "h-full w-full min-w-0 flex-1 bg-transparent pl-2 pr-4 font-sans text-base leading-6 text-foreground outline-none tabular-nums",
          "placeholder:text-muted-foreground-soft disabled:cursor-not-allowed",
        )}
      />
    </div>
  );
}